import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Chip,
  Button,
  Link,
  Alert,
  CircularProgress,
  TextField,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Search, FileDownload } from '@mui/icons-material';

interface ChangeOnboardingItem {
  application_id: string;
  application_name: string;
  platform: string;
  portfolio: string;
  app_owners: string | null;
  bca_status: boolean | null;
  mvc_status: boolean | null;
  onboarding_info: {
    onboarding_status: string;
    deployment_channel: string;
    change_process_type: string;
    last_change_date: string;
  } | null;
  owners_role_mac?: string;
  change_success_rate?: number | null;
}

interface ChangeOnboardingProps {
  changeOnboardingData: ChangeOnboardingItem[];
  filteredChangeOnboardingData: ChangeOnboardingItem[];
  changeOnboardingLoading: boolean;
  changeOnboardingError: string | null;
  changeOnboardingMvcFilter: boolean;
  setChangeOnboardingMvcFilter: (value: boolean) => void;
  changeOnboardingBcaFilter: boolean;
  setChangeOnboardingBcaFilter: (value: boolean) => void;
  changeOnboardingSearchQuery: string;
  setChangeOnboardingSearchQuery: (value: string) => void;
  changeOnboardingStatusFilter: string;
  setChangeOnboardingStatusFilter: (value: string) => void;
  changeOnboardingDeploymentChannelFilter: string;
  setChangeOnboardingDeploymentChannelFilter: (value: string) => void;
  changeOnboardingChangeProcessTypeFilter: string;
  setChangeOnboardingChangeProcessTypeFilter: (value: string) => void;
  uniqueAppIdsCount: number;
  onboardedAppsCount: number;
  redChannelAppsCount: number;
  transformPortfolioName: (portfolio: string) => string;
  formatChangeDate: (dateString: string | null | undefined) => string;
}

export const ChangeOnboarding: React.FC<ChangeOnboardingProps> = ({
  changeOnboardingData,
  filteredChangeOnboardingData,
  changeOnboardingLoading,
  changeOnboardingError,
  changeOnboardingMvcFilter,
  setChangeOnboardingMvcFilter,
  changeOnboardingBcaFilter,
  setChangeOnboardingBcaFilter,
  changeOnboardingSearchQuery,
  setChangeOnboardingSearchQuery,
  changeOnboardingStatusFilter,
  setChangeOnboardingStatusFilter,
  changeOnboardingDeploymentChannelFilter,
  setChangeOnboardingDeploymentChannelFilter,
  changeOnboardingChangeProcessTypeFilter,
  setChangeOnboardingChangeProcessTypeFilter,
  uniqueAppIdsCount,
  onboardedAppsCount,
  redChannelAppsCount,
  transformPortfolioName,
  formatChangeDate,
}) => {
  const [changeOnboardingPage, setChangeOnboardingPage] = useState(0);
  const [changeOnboardingRowsPerPage, setChangeOnboardingRowsPerPage] = useState(10);
  const [changeOnboardingSortConfig, setChangeOnboardingSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Change Onboarding Dashboard
        </Typography>

        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card sx={{ bgcolor: '#f3e5f5', border: '1px solid #ba68c8' }}>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="caption" color="textPrimary" gutterBottom>
                  Total Apps
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#7b1fa2' }}>
                  {uniqueAppIdsCount}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ bgcolor: '#e8f5e9', border: '1px solid #81c784' }}>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="caption" color="textPrimary" gutterBottom>
                  Onboarded Apps
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#388e3c' }}>
                  {onboardedAppsCount}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ bgcolor: '#fff3e0', border: '1px solid #ffb74d' }}>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="caption" color="textPrimary" gutterBottom>
                  % Onboarded Apps
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#f57c00' }}>
                  {uniqueAppIdsCount > 0 ? Math.round((onboardedAppsCount / uniqueAppIdsCount) * 100) : 0}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ bgcolor: '#ffebee', border: '1px solid #e57373' }}>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="caption" color="textPrimary" gutterBottom>
                  Red Channel Apps
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#c62828' }}>
                  {redChannelAppsCount}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters and Export Button */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={changeOnboardingMvcFilter}
                onChange={(e) => {
                  setChangeOnboardingMvcFilter(e.target.checked);
                  setChangeOnboardingPage(0);
                }}
                sx={{
                  '& .MuiSwitch-switchBase': {
                    color: '#efededff',
                    '&.Mui-checked': {
                      color: '#1976d2',
                    },
                    '&.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#1976d2',
                    },
                  },
                  '& .MuiSwitch-track': {
                    backgroundColor: '#565555ff',
                  },
                }}
              />
            }
            label="MVC"
          />

          <FormControlLabel
            control={
              <Switch
                checked={changeOnboardingBcaFilter}
                onChange={(e) => {
                  setChangeOnboardingBcaFilter(e.target.checked);
                  setChangeOnboardingPage(0);
                }}
                sx={{
                  '& .MuiSwitch-switchBase': {
                    color: '#efededff',
                    '&.Mui-checked': {
                      color: '#1976d2',
                    },
                    '&.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#1976d2',
                    },
                  },
                  '& .MuiSwitch-track': {
                    backgroundColor: '#565555ff',
                  },
                }}
              />
            }
            label="BCA"
          />

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Onboarding Status</InputLabel>
            <Select
              value={changeOnboardingStatusFilter}
              label="Onboarding Status"
              onChange={(e) => {
                setChangeOnboardingStatusFilter(e.target.value);
                setChangeOnboardingPage(0);
              }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="onboarded">Onboarded</MenuItem>
              <MenuItem value="not onboarded">Not Onboarded</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Deployment Channel</InputLabel>
            <Select
              value={changeOnboardingDeploymentChannelFilter}
              label="Deployment Channel"
              onChange={(e) => {
                setChangeOnboardingDeploymentChannelFilter(e.target.value);
                setChangeOnboardingPage(0);
              }}
            >
              <MenuItem value="all">All</MenuItem>
              {Array.from(new Set(changeOnboardingData.map(item => item.onboarding_info?.deployment_channel).filter(Boolean))).sort().map(channel => {
                const displayChannel = channel === 'red' ? 'Red' : channel === 'green' ? 'Green' : channel;
                return <MenuItem key={channel} value={channel}>{displayChannel}</MenuItem>;
              })}
              <MenuItem value="N/A">N/A</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Change Process Type</InputLabel>
            <Select
              value={changeOnboardingChangeProcessTypeFilter}
              label="Change Process Type"
              onChange={(e) => {
                setChangeOnboardingChangeProcessTypeFilter(e.target.value);
                setChangeOnboardingPage(0);
              }}
            >
              <MenuItem value="all">All</MenuItem>
              {Array.from(new Set(changeOnboardingData.map(item => item.onboarding_info?.change_process_type).filter(Boolean))).sort().map(processType => {
                const displayProcessType = processType === 'classic' ? 'Classic' : processType === 'rapid' ? 'Rapid' : processType;
                return <MenuItem key={processType} value={processType}>{displayProcessType}</MenuItem>;
              })}
              <MenuItem value="N/A">N/A</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            startIcon={<FileDownload />}
            onClick={() => {
              const headers = ['Application ID', 'Application Name', 'Platform', 'Portfolio', 'Onboarding Status', 'Deployment Channel', 'Change Process Type', 'Last Change Date', 'Change Success Rate (%)'];
              const csvRows = [headers.join(',')];
              
              // Apply the same sorting logic as the table
              let sortedData = [...filteredChangeOnboardingData];
              
              if (changeOnboardingSortConfig) {
                sortedData.sort((a, b) => {
                  let aValue: any;
                  let bValue: any;
                  
                  switch (changeOnboardingSortConfig.key) {
                    case 'onboarding_status':
                      aValue = a.onboarding_info?.onboarding_status || 'not onboarded';
                      bValue = b.onboarding_info?.onboarding_status || 'not onboarded';
                      break;
                    case 'deployment_channel':
                      aValue = a.onboarding_info?.deployment_channel || 'N/A';
                      bValue = b.onboarding_info?.deployment_channel || 'N/A';
                      break;
                    case 'change_process_type':
                      aValue = a.onboarding_info?.change_process_type || 'N/A';
                      bValue = b.onboarding_info?.change_process_type || 'N/A';
                      break;
                    case 'last_change_date':
                      aValue = a.onboarding_info?.last_change_date || '';
                      bValue = b.onboarding_info?.last_change_date || '';
                      break;
                    case 'change_success_rate':
                      aValue = a.change_success_rate ?? -1;
                      bValue = b.change_success_rate ?? -1;
                      break;
                    default:
                      aValue = a[changeOnboardingSortConfig.key as keyof ChangeOnboardingItem];
                      bValue = b[changeOnboardingSortConfig.key as keyof ChangeOnboardingItem];
                  }
                  
                  if (aValue === null || aValue === undefined) return 1;
                  if (bValue === null || bValue === undefined) return -1;
                  
                  if (typeof aValue === 'string' && typeof bValue === 'string') {
                    return changeOnboardingSortConfig.direction === 'asc'
                      ? aValue.localeCompare(bValue)
                      : bValue.localeCompare(aValue);
                  }
                  
                  return changeOnboardingSortConfig.direction === 'asc'
                    ? aValue > bValue ? 1 : -1
                    : bValue > aValue ? 1 : -1;
                });
              } else {
                // Default sorting: first by deployment channel (red first), then by onboarding status (onboarded first)
                sortedData.sort((a, b) => {
                  // First sort by deployment channel
                  const aChannel = (a.onboarding_info?.deployment_channel || 'N/A').toLowerCase();
                  const bChannel = (b.onboarding_info?.deployment_channel || 'N/A').toLowerCase();
                  
                  // Priority order: red > green > N/A
                  const channelPriority = (channel: string) => {
                    if (channel === 'red') return 0;
                    if (channel === 'green') return 1;
                    return 2; // N/A or other values
                  };
                  
                  const channelCompare = channelPriority(aChannel) - channelPriority(bChannel);
                  if (channelCompare !== 0) return channelCompare;
                  
                  // Then sort by onboarding status (onboarded first)
                  const aStatus = (a.onboarding_info?.onboarding_status || 'not onboarded').toLowerCase();
                  const bStatus = (b.onboarding_info?.onboarding_status || 'not onboarded').toLowerCase();
                  
                  // Priority order: onboarded > not onboarded
                  const statusPriority = (status: string) => {
                    return status === 'onboarded' ? 0 : 1;
                  };
                  
                  return statusPriority(aStatus) - statusPriority(bStatus);
                });
              }
              
              sortedData.forEach(item => {
                const rawStatus = item.onboarding_info ? item.onboarding_info.onboarding_status : 'not onboarded';
                const onboardingStatus = rawStatus === 'onboarded' ? 'Onboarded' : rawStatus === 'not onboarded' ? 'Not Onboarded' : rawStatus;
                const rawChannel = item.onboarding_info?.deployment_channel || 'N/A';
                const deploymentChannel = rawChannel === 'green' ? 'Green' : rawChannel === 'red' ? 'Red' : rawChannel;
                const rawProcessType = item.onboarding_info?.change_process_type || 'N/A';
                const changeProcessType = rawProcessType === 'classic' ? 'Classic' : rawProcessType === 'rapid' ? 'Rapid' : rawProcessType;
                const lastChangeDate = formatChangeDate(item.onboarding_info?.last_change_date);
                const changeSuccessRate = item.change_success_rate !== null && item.change_success_rate !== undefined ? item.change_success_rate.toFixed(2) : 'N/A';
                
                const row = [
                  item.application_id || '',
                  `"${(item.application_name || '').replace(/"/g, '""')}"`,
                  `"${(item.platform || 'Undefined').replace(/"/g, '""')}"`,
                  `"${transformPortfolioName(item.portfolio || 'Undefined').replace(/"/g, '""')}"`,
                  onboardingStatus,
                  deploymentChannel,
                  changeProcessType,
                  lastChangeDate,
                  changeSuccessRate,
                ];
                csvRows.push(row.join(','));
              });
              
              const csvContent = csvRows.join('\n');
              const blob = new Blob([csvContent], { type: 'text/csv' });
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `change_onboarding_${new Date().toISOString().split('T')[0]}.csv`;
              link.click();
              window.URL.revokeObjectURL(url);
            }}
            sx={{
              borderColor: '#00A8CC',
              color: '#00A8CC',
              '&:hover': {
                borderColor: '#0067b3',
                backgroundColor: 'rgba(0, 168, 204, 0.04)',
              },
            }}
          >
            Export CSV
          </Button>
        </Box>

        {/* Search Bar - Full Width */}
        <Box sx={{ mb: 2 }}>
          <TextField
            size="small"
            placeholder="Search by App ID, Name, Portfolio, Platform..."
            value={changeOnboardingSearchQuery}
            onChange={(e) => {
              setChangeOnboardingSearchQuery(e.target.value);
              setChangeOnboardingPage(0);
            }}
            InputProps={{
              startAdornment: <Search sx={{ color: '#6c757d', mr: 1 }} />,
            }}
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f8f9fa',
              },
            }}
          />
        </Box>
      </Box>

      {/* Loading, Error, and Data Display */}
      {changeOnboardingLoading ? (
        <Paper
          elevation={0}
          sx={{
            border: '1px dashed #c5c8d1',
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 280,
            mb: 3,
            bgcolor: '#fafbff',
          }}
        >
          <CircularProgress size={28} sx={{ mb: 2 }} />
          <Typography variant="body1" color="textSecondary">
            Loading change onboarding insights...
          </Typography>
        </Paper>
      ) : changeOnboardingError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {changeOnboardingError}
        </Alert>
      ) : filteredChangeOnboardingData.length > 0 ? (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              To onboard your application for change visibility, please visit the{' '}
              <Link href="https://flux.maersk.io/application-onboarding" target="_blank" rel="noopener noreferrer">
                Application Onboarding page on Flux
              </Link>
              .
            </Typography>
          </Alert>
          <TableContainer component={Paper} sx={{ border: '1px solid #e1e5e9' }}>
            <Table>
              <TableHead sx={{ backgroundColor: '#f8f9fa' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: '13px', color: '#2c3e50' }}>
                  <TableSortLabel
                    active={changeOnboardingSortConfig?.key === 'application_id'}
                    direction={changeOnboardingSortConfig?.key === 'application_id' ? changeOnboardingSortConfig.direction : 'asc'}
                    onClick={() => {
                      const isAsc = changeOnboardingSortConfig?.key === 'application_id' && changeOnboardingSortConfig.direction === 'asc';
                      setChangeOnboardingSortConfig({ key: 'application_id', direction: isAsc ? 'desc' : 'asc' });
                    }}
                  >
                    Application ID
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '13px', color: '#2c3e50' }}>
                  <TableSortLabel
                    active={changeOnboardingSortConfig?.key === 'application_name'}
                    direction={changeOnboardingSortConfig?.key === 'application_name' ? changeOnboardingSortConfig.direction : 'asc'}
                    onClick={() => {
                      const isAsc = changeOnboardingSortConfig?.key === 'application_name' && changeOnboardingSortConfig.direction === 'asc';
                      setChangeOnboardingSortConfig({ key: 'application_name', direction: isAsc ? 'desc' : 'asc' });
                    }}
                  >
                    Application Name
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '13px', color: '#2c3e50' }}>
                  <TableSortLabel
                    active={changeOnboardingSortConfig?.key === 'platform'}
                    direction={changeOnboardingSortConfig?.key === 'platform' ? changeOnboardingSortConfig.direction : 'asc'}
                    onClick={() => {
                      const isAsc = changeOnboardingSortConfig?.key === 'platform' && changeOnboardingSortConfig.direction === 'asc';
                      setChangeOnboardingSortConfig({ key: 'platform', direction: isAsc ? 'desc' : 'asc' });
                    }}
                  >
                    Platform
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '13px', color: '#2c3e50' }}>
                  <TableSortLabel
                    active={changeOnboardingSortConfig?.key === 'portfolio'}
                    direction={changeOnboardingSortConfig?.key === 'portfolio' ? changeOnboardingSortConfig.direction : 'asc'}
                    onClick={() => {
                      const isAsc = changeOnboardingSortConfig?.key === 'portfolio' && changeOnboardingSortConfig.direction === 'asc';
                      setChangeOnboardingSortConfig({ key: 'portfolio', direction: isAsc ? 'desc' : 'asc' });
                    }}
                  >
                    Portfolio
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '13px', color: '#2c3e50' }}>
                  <TableSortLabel
                    active={changeOnboardingSortConfig?.key === 'onboarding_status'}
                    direction={changeOnboardingSortConfig?.key === 'onboarding_status' ? changeOnboardingSortConfig.direction : 'asc'}
                    onClick={() => {
                      const isAsc = changeOnboardingSortConfig?.key === 'onboarding_status' && changeOnboardingSortConfig.direction === 'asc';
                      setChangeOnboardingSortConfig({ key: 'onboarding_status', direction: isAsc ? 'desc' : 'asc' });
                    }}
                  >
                    Onboarding Status
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '13px', color: '#2c3e50' }}>
                  <TableSortLabel
                    active={changeOnboardingSortConfig?.key === 'deployment_channel'}
                    direction={changeOnboardingSortConfig?.key === 'deployment_channel' ? changeOnboardingSortConfig.direction : 'asc'}
                    onClick={() => {
                      const isAsc = changeOnboardingSortConfig?.key === 'deployment_channel' && changeOnboardingSortConfig.direction === 'asc';
                      setChangeOnboardingSortConfig({ key: 'deployment_channel', direction: isAsc ? 'desc' : 'asc' });
                    }}
                  >
                    Deployment Channel
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '13px', color: '#2c3e50' }}>
                  <TableSortLabel
                    active={changeOnboardingSortConfig?.key === 'change_process_type'}
                    direction={changeOnboardingSortConfig?.key === 'change_process_type' ? changeOnboardingSortConfig.direction : 'asc'}
                    onClick={() => {
                      const isAsc = changeOnboardingSortConfig?.key === 'change_process_type' && changeOnboardingSortConfig.direction === 'asc';
                      setChangeOnboardingSortConfig({ key: 'change_process_type', direction: isAsc ? 'desc' : 'asc' });
                    }}
                  >
                    Change Process Type
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '13px', color: '#2c3e50' }}>
                  <TableSortLabel
                    active={changeOnboardingSortConfig?.key === 'last_change_date'}
                    direction={changeOnboardingSortConfig?.key === 'last_change_date' ? changeOnboardingSortConfig.direction : 'asc'}
                    onClick={() => {
                      const isAsc = changeOnboardingSortConfig?.key === 'last_change_date' && changeOnboardingSortConfig.direction === 'asc';
                      setChangeOnboardingSortConfig({ key: 'last_change_date', direction: isAsc ? 'desc' : 'asc' });
                    }}
                  >
                    Last Change Date
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '13px', color: '#2c3e50' }}>
                  <TableSortLabel
                    active={changeOnboardingSortConfig?.key === 'change_success_rate'}
                    direction={changeOnboardingSortConfig?.key === 'change_success_rate' ? changeOnboardingSortConfig.direction : 'asc'}
                    onClick={() => {
                      const isAsc = changeOnboardingSortConfig?.key === 'change_success_rate' && changeOnboardingSortConfig.direction === 'asc';
                      setChangeOnboardingSortConfig({ key: 'change_success_rate', direction: isAsc ? 'desc' : 'asc' });
                    }}
                  >
                    Change Success Rate (%)
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                let sortedData = [...filteredChangeOnboardingData];
                
                if (changeOnboardingSortConfig) {
                  sortedData.sort((a, b) => {
                    let aValue: any;
                    let bValue: any;
                    
                    switch (changeOnboardingSortConfig.key) {
                      case 'onboarding_status':
                        aValue = a.onboarding_info?.onboarding_status || 'not onboarded';
                        bValue = b.onboarding_info?.onboarding_status || 'not onboarded';
                        break;
                      case 'deployment_channel':
                        aValue = a.onboarding_info?.deployment_channel || 'N/A';
                        bValue = b.onboarding_info?.deployment_channel || 'N/A';
                        break;
                      case 'change_process_type':
                        aValue = a.onboarding_info?.change_process_type || 'N/A';
                        bValue = b.onboarding_info?.change_process_type || 'N/A';
                        break;
                      case 'last_change_date':
                        aValue = a.onboarding_info?.last_change_date || '';
                        bValue = b.onboarding_info?.last_change_date || '';
                        break;
                      case 'change_success_rate':
                        aValue = a.change_success_rate ?? -1;
                        bValue = b.change_success_rate ?? -1;
                        break;
                      default:
                        aValue = a[changeOnboardingSortConfig.key as keyof ChangeOnboardingItem];
                        bValue = b[changeOnboardingSortConfig.key as keyof ChangeOnboardingItem];
                    }
                    
                    if (aValue === null || aValue === undefined) return 1;
                    if (bValue === null || bValue === undefined) return -1;
                    
                    if (typeof aValue === 'string' && typeof bValue === 'string') {
                      return changeOnboardingSortConfig.direction === 'asc'
                        ? aValue.localeCompare(bValue)
                        : bValue.localeCompare(aValue);
                    }
                    
                    return changeOnboardingSortConfig.direction === 'asc'
                      ? aValue > bValue ? 1 : -1
                      : bValue > aValue ? 1 : -1;
                  });
                } else {
                  // Default sorting: first by deployment channel (red first), then by onboarding status (onboarded first)
                  sortedData.sort((a, b) => {
                    // First sort by deployment channel
                    const aChannel = (a.onboarding_info?.deployment_channel || 'N/A').toLowerCase();
                    const bChannel = (b.onboarding_info?.deployment_channel || 'N/A').toLowerCase();
                    
                    // Priority order: red > green > N/A
                    const channelPriority = (channel: string) => {
                      if (channel === 'red') return 0;
                      if (channel === 'green') return 1;
                      return 2; // N/A or other values
                    };
                    
                    const channelCompare = channelPriority(aChannel) - channelPriority(bChannel);
                    if (channelCompare !== 0) return channelCompare;
                    
                    // Then sort by onboarding status (onboarded first)
                    const aStatus = (a.onboarding_info?.onboarding_status || 'not onboarded').toLowerCase();
                    const bStatus = (b.onboarding_info?.onboarding_status || 'not onboarded').toLowerCase();
                    
                    // Priority order: onboarded > not onboarded
                    const statusPriority = (status: string) => {
                      return status === 'onboarded' ? 0 : 1;
                    };
                    
                    return statusPriority(aStatus) - statusPriority(bStatus);
                  });
                }
                
                const paginatedData = sortedData.slice(
                  changeOnboardingPage * changeOnboardingRowsPerPage,
                  changeOnboardingPage * changeOnboardingRowsPerPage + changeOnboardingRowsPerPage
                );
                
                return paginatedData.map((item, index) => {
                  const appId = item.application_id || '';
                  const isValidAppId = appId && appId.trim() !== '' && appId.toUpperCase() !== 'N/A' && appId.toUpperCase() !== 'NA';
                  const rawStatus = item.onboarding_info ? item.onboarding_info.onboarding_status : 'not onboarded';
                  const onboardingStatus = rawStatus === 'onboarded' ? 'Onboarded' : rawStatus === 'not onboarded' ? 'Not Onboarded' : rawStatus;
                  const rawChannel = item.onboarding_info?.deployment_channel || 'N/A';
                  const deploymentChannel = rawChannel === 'green' ? 'Green' : rawChannel === 'red' ? 'Red' : rawChannel;
                  const rawProcessType = item.onboarding_info?.change_process_type || 'N/A';
                  const changeProcessType = rawProcessType === 'classic' ? 'Classic' : rawProcessType === 'rapid' ? 'Rapid' : rawProcessType;
                  const lastChangeDate = formatChangeDate(item.onboarding_info?.last_change_date);
                  
                  return (
                    <TableRow 
                      key={index}
                      sx={{ 
                        '&:nth-of-type(even)': { backgroundColor: '#fafafa' },
                        '&:hover': { backgroundColor: '#f0f7ff' }
                      }}
                    >
                      <TableCell sx={{ fontSize: '12px' }}>
                        {isValidAppId ? (
                          <Link
                            href={`https://msa.maersk.io/asset_lookup?applicationId=${appId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ textDecoration: 'none', color: '#1976d2', '&:hover': { textDecoration: 'underline' } }}
                          >
                            {appId}
                          </Link>
                        ) : (
                          appId || 'N/A'
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: '12px' }}>{item.application_name || 'N/A'}</TableCell>
                      <TableCell sx={{ fontSize: '12px' }}>{item.platform || 'Undefined'}</TableCell>
                      <TableCell sx={{ fontSize: '12px' }}>{transformPortfolioName(item.portfolio || 'Undefined')}</TableCell>
                      <TableCell>
                        <Chip
                          label={onboardingStatus}
                          size="small"
                          sx={{
                            bgcolor: onboardingStatus === 'Onboarded' ? '#c8e6c9 !important' : '#e0e0e0 !important',
                            color: onboardingStatus === 'Onboarded' ? '#2e7d32 !important' : '#666666 !important',
                            fontWeight: 600,
                            fontSize: '11px',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={deploymentChannel}
                          size="small"
                          sx={{
                            bgcolor: deploymentChannel === 'Green' ? '#c8e6c9 !important' : 
                                    deploymentChannel === 'Red' ? '#ffcdd2 !important' : 
                                    deploymentChannel === 'N/A' ? '#e0e0e0 !important' : '#c8e6c9 !important',
                            color: deploymentChannel === 'Green' ? '#2e7d32 !important' : 
                                   deploymentChannel === 'Red' ? '#c62828 !important' : 
                                   deploymentChannel === 'N/A' ? '#666666 !important' : '#2e7d32 !important',
                            fontWeight: 600,
                            fontSize: '11px',
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '12px' }}>{changeProcessType}</TableCell>
                      <TableCell sx={{ fontSize: '12px' }}>{lastChangeDate}</TableCell>
                      <TableCell sx={{ fontSize: '12px' }}>
                        {item.change_success_rate !== null && item.change_success_rate !== undefined 
                          ? `${item.change_success_rate.toFixed(2)}%` 
                          : 'N/A'}
                      </TableCell>
                    </TableRow>
                  );
                });
              })()}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filteredChangeOnboardingData.length}
            rowsPerPage={changeOnboardingRowsPerPage}
            page={changeOnboardingPage}
            onPageChange={(event, newPage) => setChangeOnboardingPage(newPage)}
            onRowsPerPageChange={(event) => {
              setChangeOnboardingRowsPerPage(parseInt(event.target.value, 10));
              setChangeOnboardingPage(0);
            }}
          />
        </TableContainer>
        </Box>
      ) : (
        <Box sx={{ p: 4, textAlign: 'center', backgroundColor: '#f9fafb', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            No change onboarding data available
          </Typography>
        </Box>
      )}
    </Box>
  );
};
